import { useState, useEffect } from 'react';
import { avisos as avisosApi } from '../services/api';
import {
    Bell, FileText, Eye, Archive, Trash2, AlertTriangle,
    AlertCircle, Info, Clock, Users, Filter, Search,
    ChevronDown, MoreVertical, Check, X, Loader2, Calendar, Sparkles
} from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const PRIORIDADES = {
    normal: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Normal' },
    importante: { icon: AlertCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Importante' },
    urgente: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Urgente' },
};

const ESTADOS = {
    borrador: { color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Borrador' },
    publicado: { color: 'text-green-400', bg: 'bg-green-500/10', label: 'Publicado' },
    archivado: { color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Archivado' },
};

const HistorialAvisos = () => {
    const [avisos, setAvisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        estado: '',
        prioridad: '',
        search: '',
    });
    const [selectedAviso, setSelectedAviso] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [cleanupMessage, setCleanupMessage] = useState('');
    const [showCleanupModal, setShowCleanupModal] = useState(false);

    useEffect(() => {
        fetchAvisos();
    }, [filters.estado, filters.prioridad]);

    const fetchAvisos = async () => {
        try {
            setLoading(true);
            const data = await avisosApi.getAll({
                estado: filters.estado || undefined,
                prioridad: filters.prioridad || undefined
            });
            setAvisos(data);
        } catch (err) {
            console.error('Error fetching avisos:', err);
            setError('Error al cargar los avisos');
        } finally {
            setLoading(false);
        }
    };

    const handleArchivar = async (id) => {
        try {
            setActionLoading(true);
            await avisosApi.archivar(id);
            fetchAvisos();
        } catch (err) {
            console.error('Error archiving aviso:', err);
            setError('Error al archivar el aviso');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedAviso) return;
        try {
            setActionLoading(true);
            await avisosApi.delete(selectedAviso.id);
            setShowDeleteModal(false);
            setSelectedAviso(null);
            fetchAvisos();
        } catch (err) {
            console.error('Error deleting aviso:', err);
            setError('Error al eliminar el aviso');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePublicar = async (id) => {
        try {
            setActionLoading(true);
            await avisosApi.publicar(id);
            fetchAvisos();
        } catch (err) {
            console.error('Error publishing aviso:', err);
            setError('Error al publicar el aviso');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCleanupClick = () => {
        setShowCleanupModal(true);
    };

    const handleCleanup = async () => {
        try {
            setCleanupLoading(true);
            setCleanupMessage('');
            const result = await avisosApi.cleanup();
            setCleanupMessage(result.message);
            setTimeout(() => setCleanupMessage(''), 10000);
        } catch (err) {
            console.error('Error cleaning up:', err);
            setError('Error al limpiar avisos caducados');
        } finally {
            setCleanupLoading(false);
        }
    };

    const filteredAvisos = avisos.filter(aviso => {
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            return aviso.titulo.toLowerCase().includes(searchLower) ||
                aviso.contenido.toLowerCase().includes(searchLower);
        }
        return true;
    });

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getPrioridadInfo = (prioridad) => PRIORIDADES[prioridad] || PRIORIDADES.normal;
    const getEstadoInfo = (estado) => ESTADOS[estado] || ESTADOS.borrador;

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <Bell className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Historial de Avisos</h1>
                        <p className="text-gray-400">Gestiona todos los comunicados enviados</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleCleanupClick}
                        disabled={cleanupLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg transition-colors disabled:opacity-50"
                        title="Limpiar adjuntos de avisos caducados"
                    >
                        {cleanupLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4" />
                        )}
                        Limpiar caducados
                    </button>
                    <a
                        href="/admin/avisos"
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-white rounded-lg transition-colors"
                    >
                        <Bell className="w-4 h-4" />
                        Nuevo Aviso
                    </a>
                </div>
            </div>

            {/* Error message */}
            {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400">{error}</span>
                    <button onClick={() => setError('')} className="ml-auto">
                        <X className="w-4 h-4 text-red-400" />
                    </button>
                </div>
            )}

            {/* Cleanup success message */}
            {cleanupMessage && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-400">{cleanupMessage}</span>
                    <button onClick={() => setCleanupMessage('')} className="ml-auto">
                        <X className="w-4 h-4 text-green-400" />
                    </button>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                <div className="flex flex-wrap gap-4">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Buscar avisos..."
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                            />
                        </div>
                    </div>

                    {/* Estado filter */}
                    <div className="relative">
                        <select
                            value={filters.estado}
                            onChange={(e) => setFilters(prev => ({ ...prev, estado: e.target.value }))}
                            className="appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 cursor-pointer"
                        >
                            <option value="" className="bg-[#1a1a2e] text-white">Todos los estados</option>
                            <option value="borrador" className="bg-[#1a1a2e] text-white">Borradores</option>
                            <option value="publicado" className="bg-[#1a1a2e] text-white">Publicados</option>
                            <option value="archivado" className="bg-[#1a1a2e] text-white">Archivados</option>
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Prioridad filter */}
                    <div className="relative">
                        <select
                            value={filters.prioridad}
                            onChange={(e) => setFilters(prev => ({ ...prev, prioridad: e.target.value }))}
                            className="appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 cursor-pointer"
                        >
                            <option value="" className="bg-[#1a1a2e] text-white">Todas las prioridades</option>
                            <option value="normal" className="bg-[#1a1a2e] text-white">Normal</option>
                            <option value="importante" className="bg-[#1a1a2e] text-white">Importante</option>
                            <option value="urgente" className="bg-[#1a1a2e] text-white">Urgente</option>
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{avisos.length}</p>
                            <p className="text-sm text-gray-400">Total avisos</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {avisos.filter(a => a.estado === 'publicado').length}
                            </p>
                            <p className="text-sm text-gray-400">Publicados</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-500/10 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {avisos.filter(a => a.estado === 'borrador').length}
                            </p>
                            <p className="text-sm text-gray-400">Borradores</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                            <Archive className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {avisos.filter(a => a.estado === 'archivado').length}
                            </p>
                            <p className="text-sm text-gray-400">Archivados</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Avisos List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                </div>
            ) : filteredAvisos.length === 0 ? (
                <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
                    <Bell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No hay avisos que mostrar</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredAvisos.map(aviso => {
                        const prioridadInfo = getPrioridadInfo(aviso.prioridad);
                        const estadoInfo = getEstadoInfo(aviso.estado);
                        const PrioridadIcon = prioridadInfo.icon;

                        return (
                            <div
                                key={aviso.id}
                                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all"
                            >
                                <div className="flex items-start gap-4">
                                    {/* Priority icon */}
                                    <div className={`w-10 h-10 rounded-lg ${prioridadInfo.bg} flex items-center justify-center flex-shrink-0`}>
                                        <PrioridadIcon className={`w-5 h-5 ${prioridadInfo.color}`} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <h3 className="text-lg font-semibold text-white mb-1">
                                                    {aviso.titulo}
                                                </h3>
                                                <p className="text-gray-400 text-sm line-clamp-2">
                                                    {aviso.contenido}
                                                </p>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {aviso.estado === 'borrador' && (
                                                    <button
                                                        onClick={() => handlePublicar(aviso.id)}
                                                        disabled={actionLoading}
                                                        className="px-3 py-1.5 bg-green-500/10 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/20 transition-colors"
                                                    >
                                                        Publicar
                                                    </button>
                                                )}
                                                {aviso.estado === 'publicado' && (
                                                    <button
                                                        onClick={() => handleArchivar(aviso.id)}
                                                        disabled={actionLoading}
                                                        className="p-2 text-gray-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                                                        title="Archivar"
                                                    >
                                                        <Archive className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setSelectedAviso(aviso);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Meta */}
                                        <div className="flex flex-wrap items-center gap-4 mt-3">
                                            {/* Estado */}
                                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${estadoInfo.bg} ${estadoInfo.color}`}>
                                                {estadoInfo.label}
                                            </span>

                                            {/* Prioridad */}
                                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${prioridadInfo.bg} ${prioridadInfo.color}`}>
                                                {prioridadInfo.label}
                                            </span>

                                            {/* Líneas */}
                                            {aviso.lineas && (
                                                <span className="text-xs text-gray-500">
                                                    Líneas: {aviso.lineas}
                                                </span>
                                            )}

                                            {/* Conductores */}
                                            {aviso.conductores && (
                                                <span className="text-xs text-orange-500/80">
                                                    Conductores: {aviso.conductores}
                                                </span>
                                            )}

                                            {/* Adjuntos */}
                                            {aviso.num_adjuntos > 0 && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                    <FileText className="w-3 h-3" />
                                                    {aviso.num_adjuntos} adjunto{aviso.num_adjuntos !== 1 ? 's' : ''}
                                                </span>
                                            )}

                                            {/* Lecturas */}
                                            <span className="flex items-center gap-1 text-xs text-gray-500">
                                                <Eye className="w-3 h-3" />
                                                {aviso.num_lecturas || 0} lectura{aviso.num_lecturas !== 1 ? 's' : ''}
                                            </span>

                                            {/* Fecha */}
                                            <span className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
                                                <Calendar className="w-3 h-3" />
                                                {aviso.fecha_publicacion
                                                    ? formatDate(aviso.fecha_publicacion)
                                                    : formatDate(aviso.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && selectedAviso && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                                <Trash2 className="w-6 h-6 text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">Eliminar aviso</h3>
                                <p className="text-sm text-gray-400">Esta acción no se puede deshacer</p>
                            </div>
                        </div>

                        <p className="text-gray-300 mb-6">
                            ¿Estás seguro de que quieres eliminar el aviso "{selectedAviso.titulo}"?
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedAviso(null);
                                }}
                                className="flex-1 px-4 py-2 bg-white/5 text-gray-300 rounded-lg hover:bg-white/10 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={actionLoading}
                                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {actionLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Eliminando...
                                    </>
                                ) : (
                                    'Eliminar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cleanup Confirmation Modal */}
            <ConfirmModal
                isOpen={showCleanupModal}
                onClose={() => setShowCleanupModal(false)}
                onConfirm={handleCleanup}
                title="Limpiar Avisos Caducados"
                message="¿Limpiar adjuntos de avisos caducados y archivados?

Esto eliminará los archivos PDF/imágenes asociados a avisos que ya han caducado o están archivados."
                confirmText="Limpiar"
                variant="warning"
            />
        </div>
    );
};

export default HistorialAvisos;
